/**
 * GroupSummary.jsx - what a main project (or a sub-project) holds, on its
 * page (10/14/26): the roll-up figures and every record under it -
 * sub-projects with their subtotal, then their PO lines - with PO $,
 * invoiced, leftover and status. Optionally the Tracking roll-up.
 *
 * The figures add up the Won / Complete records (the work); an open, lost or
 * cancelled bid in the group is listed, greyed, and left out of them - the
 * same records the Projects tab adds up.
 */
import { useState } from 'react';
import { fmt } from '../../../utils/format';
import { leftoverOf } from '../../../utils/projectMoney';
import { isProjectRow } from '../../../data/projectStatus';
import { rollupRow } from '../../../data/projectHierarchy';
import ActiveClosedPill, { projectStatus } from '../ActiveClosedPill';
import { StatusPill } from '../../ui/StatusPill';
import TrackingRollup from './TrackingRollup';

const th = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 800,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  padding: '7px 8px',
  borderBottom: '1px solid #E5E9DD',
  whiteSpace: 'nowrap',
  background: '#F6F8F3',
};
const td = { fontSize: 12, padding: '7px 8px', borderBottom: '1px solid #F0F2EC', verticalAlign: 'top' };
const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const mono = { ...td, fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 11, whiteSpace: 'nowrap' };
const money = (v) => (v === null || v === undefined || v === '' ? '-' : fmt(v));
const pick = (r, a, b) => r[a] ?? r[b];

function Card({ label, value, tone }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 8, padding: '8px 12px', minWidth: 0, flex: '1 1 140px' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 3 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: tone || '#0D0D0D',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** The figures a group page shows, over its Won / Complete records. PURE. */
export function groupFigures(records) {
  const work = (records || []).filter(isProjectRow);
  const r = rollupRow(work, { key: 'page' });
  return {
    count: work.length,
    others: (records || []).length - work.length,
    quoted: r.quotedPrice,
    po: r.poValue,
    invoiced: r.invoicedAlready,
    leftover: r.__group.leftover,
  };
}

/**
 * Props:
 *   main       the main project (data/projectHierarchy buildGroups) - its
 *              records and sub-projects
 *   sub        a sub-project of it, to show that one only (optional)
 *   currentId  the record the page is open on (marked "this record")
 *   onOpen(id) open a record
 *   tracking   show the Tracking roll-up (people who may see Projects)
 */
export default function GroupSummary({ main, sub = null, currentId = null, onOpen, tracking = false }) {
  const [showTracking, setShowTracking] = useState(false);
  const records = sub ? sub.records : main.members;
  const F = groupFigures(records);
  // Sage's order: by project number, then PO #
  const byPo = (a, b) =>
    String(pick(a, 'poNumber', 'po_number') || '').localeCompare(String(pick(b, 'poNumber', 'po_number') || ''), undefined, {
      numeric: true,
    });
  const subs = sub
    ? []
    : [...main.subs.values()].sort((a, b) => String(a.gpn || '').localeCompare(String(b.gpn || ''), undefined, { numeric: true }));
  const rowOf = (r, depth, label) => {
    const work = isProjectRow(r);
    const left = leftoverOf(r);
    const me = currentId != null && String(r.id) === String(currentId);
    return (
      <tr
        key={r.id}
        onClick={() => onOpen && !me && onOpen(r.id)}
        style={{ cursor: onOpen && !me ? 'pointer' : 'default', opacity: work ? 1 : 0.55, background: me ? '#FFFBEB' : undefined }}
        title={work ? undefined : 'Not won work - left out of the figures'}
      >
        <td style={mono}>
          <span style={{ paddingLeft: depth * 14 }}>
            {depth > 0 ? '└ ' : ''}
            {pick(r, 'globalProjectNumber', 'global_project_number') || '-'}
          </span>
        </td>
        <td style={td}>
          <div style={{ fontWeight: 700 }}>
            {label || r.name || r.description || r.id}
            {me && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, color: '#B45309' }}>THIS RECORD</span>}
          </div>
          {r.description && r.description !== (label || r.name) && <div style={{ fontSize: 11, color: '#666' }}>{r.description}</div>}
        </td>
        <td style={mono}>{pick(r, 'poNumber', 'po_number') || '-'}</td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>{String(pick(r, 'poDateIssued', 'po_date_issued') || '').slice(0, 10) || '-'}</td>
        <td style={num}>{money(pick(r, 'poValue', 'po_value'))}</td>
        <td style={num}>{money(pick(r, 'invoicedAlready', 'invoiced_already'))}</td>
        <td style={{ ...num, color: left > 0 ? '#92400E' : left < 0 ? '#B71C1C' : undefined, fontWeight: left ? 700 : 400 }}>
          {left == null ? '-' : fmt(left)}
        </td>
        <td style={td}>{work ? <ActiveClosedPill value={projectStatus(r)} /> : <StatusPill status={r.status} />}</td>
      </tr>
    );
  };
  const subHead = (s) => {
    const f = groupFigures(s.records);
    return (
      <tr key={`h-${s.key}`} style={{ background: '#F1F6EB' }}>
        <td style={{ ...mono, fontWeight: 800 }}>{s.gpn || '-'}</td>
        <td style={{ ...td, fontWeight: 800 }}>
          {s.label || 'Sub-project'} <span style={{ fontSize: 10, color: '#1B5E20' }}>({s.records.length} records)</span>
        </td>
        <td style={td} />
        <td style={td} />
        <td style={{ ...num, fontWeight: 800 }}>{money(f.po)}</td>
        <td style={{ ...num, fontWeight: 800 }}>{money(f.invoiced)}</td>
        <td style={{ ...num, fontWeight: 800 }}>{f.leftover ? fmt(f.leftover) : '-'}</td>
        <td style={td} />
      </tr>
    );
  };
  const body = [];
  if (sub) [...sub.records].sort(byPo).forEach((r) => body.push(rowOf(r, 0)));
  else {
    main.mainRecords.forEach((r) => body.push(rowOf(r, 0)));
    for (const s of subs) {
      if (s.records.length < 2) {
        s.records.forEach((r) => body.push(rowOf(r, main.mainRecords.length ? 1 : 0)));
        continue;
      }
      body.push(subHead(s));
      // the sub-project's own record first, then its lines
      [s.head, ...s.records.filter((r) => r !== s.head).sort(byPo)]
        .filter((r) => s.records.includes(r))
        .forEach((r, i) => body.push(rowOf(r, i === 0 ? 1 : 2)));
    }
  }
  const work = records.filter(isProjectRow);

  return (
    <div data-testid="group-summary">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Card
          label={sub ? 'PO lines' : 'Records'}
          value={`${F.count}${F.others ? ` (+${F.others} bid${F.others === 1 ? '' : 's'})` : ''}`}
        />
        {!sub && <Card label="Sub-projects" value={String(subs.length)} />}
        <Card label="Quoted" value={money(F.quoted)} />
        <Card label="PO $" value={money(F.po)} />
        <Card label="Invoiced" value={money(F.invoiced)} />
        <Card
          label="Leftover"
          value={F.leftover ? fmt(F.leftover) : '-'}
          tone={F.leftover > 0 ? '#92400E' : F.leftover < 0 ? '#B71C1C' : undefined}
        />
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #E5E9DD', borderRadius: 8, background: '#fff' }}>
        <table
          style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}
          aria-label={sub ? 'PO lines' : 'Sub-projects and PO lines'}
        >
          <thead>
            <tr>
              <th style={th}>Project #</th>
              <th style={th}>Name</th>
              <th style={th}>PO #</th>
              <th style={th}>PO date</th>
              <th style={{ ...th, textAlign: 'right' }}>PO $</th>
              <th style={{ ...th, textAlign: 'right' }}>Invoiced</th>
              <th style={{ ...th, textAlign: 'right' }}>Leftover</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {tracking && work.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 12, fontWeight: 800, color: '#1B5E20', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Tracking roll-up
            </h3>
            <button
              type="button"
              onClick={() => setShowTracking((v) => !v)}
              style={{
                background: '#fff',
                color: '#1B5E20',
                border: '1px solid #1B5E20',
                padding: '5px 12px',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 11,
                cursor: 'pointer',
                fontFamily: 'Montserrat',
              }}
            >
              {showTracking ? 'Hide' : `Add up the tracking of ${work.length} records`}
            </button>
          </div>
          {showTracking && <TrackingRollup records={work} onOpen={onOpen} />}
        </div>
      )}
    </div>
  );
}
